<template>
  <div>
    <div v-if="isLoading">
      <SkeletonLoader class="w-full h-6" />
      <SkeletonLoader class="w-full h-[128px] mt-4" />
    </div>
    <div v-else class="space-y-4">
      <div
        class="mt-2 lg:mt-0 lg:border-t border-border-divider border-dashed"
      />
      <V2ProductListFacet :title="$t('products.devices')">
        <V2ProductListFacetWrapper
          :total-facets="
            flattenAndCountChildren(
              props.facetData?.[
                `category_product_device_${locale}` as keyof typeof props
              ]
            )
          "
        >
          <div
            v-if="
              props.facetData?.[`category_product_device_${locale}`] &&
              Object.keys(
                props.facetData?.[`category_product_device_${locale}`]
              ).length > 0
            "
          >
            <div
              v-for="(node, key) in props.facetData?.[
                `category_product_device_${locale}` as keyof typeof props
              ]"
              :key="key"
            >
              <ProductListFacetTreeNode
                :type="`category_product_device_${locale}`"
                :node="node"
                :show-checkbox="false"
                :counter="1"
              />
            </div>
          </div>
        </V2ProductListFacetWrapper>
      </V2ProductListFacet>

      <div class="border-t border-border-divider border-dashed mb-4" />
      <V2ProductListFacet :title="$t('products.styles')">
        <V2ProductListFacetWrapper
          :total-facets="
            flattenAndCountChildren(
              props.facetData?.[
                `category_artwork_style_${locale}` as keyof typeof props
              ]
            )
          "
        >
          <div
            v-if="
              props.facetData?.[`category_artwork_style_${locale}`] &&
              Object.keys(props.facetData?.[`category_artwork_style_${locale}`])
                .length > 0
            "
          >
            <div
              v-for="(node, key) in props.facetData?.[
                `category_artwork_style_${locale}` as keyof typeof props
              ]"
              :key="key"
            >
              <ProductListFacetTreeNode
                :type="`category_artwork_style_${locale}`"
                :node="node"
                :show-checkbox="false"
                :counter="1"
              />
            </div>
          </div>
        </V2ProductListFacetWrapper>
      </V2ProductListFacet>

      <div class="border-t border-border-divider border-dashed mb-4" />
      <V2ProductListFilterToggle
        :checked="personalizable"
        :title="`${$t('products.personalization')}`"
        @change="() => togglePersonalizable()"
      />

      <div class="border-t border-border-divider border-dashed mb-4" />
      <V2ProductListFacet :title="$t('products.collabs')">
        <V2ProductListFacetWrapper
          :total-facets="
            flattenAndCountChildren(
              props.facetData?.[
                `category_artwork_collabs_${locale}` as keyof typeof props
              ]
            )
          "
        >
          <div
            v-if="
              props.facetData?.[`category_artwork_collabs_${locale}`] &&
              Object.keys(
                props.facetData?.[`category_artwork_collabs_${locale}`]
              ).length > 0
            "
          >
            <div
              v-for="(node, key) in props.facetData?.[
                `category_artwork_collabs_${locale}` as keyof typeof props
              ]"
              :key="key"
            >
              <ProductListFacetTreeNode
                :type="`category_artwork_collabs_${locale}`"
                :node="node"
                :show-checkbox="false"
                :counter="1"
              />
            </div>
          </div>
        </V2ProductListFacetWrapper>
      </V2ProductListFacet>

      <div class="border-t border-border-divider border-dashed mb-4" />
      <V2ProductListFacet :title="$t('products.series')">
        <V2ProductListFacetWrapper>
          <div
            v-if="
              props.facetData?.[`category_product_cases_${locale}`] &&
              Object.keys(props.facetData?.[`category_product_cases_${locale}`])
                .length > 0
            "
          >
            <div
              v-for="(node, key) in props.facetData?.[
                `category_product_cases_${locale}` as keyof typeof props
              ]"
              :key="key"
            >
              <ProductListFacetTreeNode
                :type="`category_product_cases_${locale}`"
                :node="node"
                :show-checkbox="false"
                :counter="1"
              />
            </div>
          </div>
        </V2ProductListFacetWrapper>
      </V2ProductListFacet>

      <div class="border-t border-border-divider border-dashed mb-4" />
      <V2ProductListFacet :title="$t('products.color')">
        <V2ProductListFacetWrapper>
          <div
            v-if="
              props.facetData?.[
                `category_artwork_color_and_pattern_${locale}`
              ] &&
              Object.keys(
                props.facetData?.[
                  `category_artwork_color_and_pattern_${locale}`
                ]
              ).length > 0
            "
          >
            <div
              v-for="(node, key) in props.facetData?.[
                `category_artwork_color_and_pattern_${locale}` as keyof typeof props
              ]"
              :key="key"
            >
              <ProductListFacetTreeNode
                :type="`category_artwork_color_and_pattern_${locale}`"
                grid
                :node="node"
                :show-checkbox="false"
                :counter="1"
              />
            </div>
          </div>
        </V2ProductListFacetWrapper>
      </V2ProductListFacet>
    </div>
  </div>
</template>

<script setup lang="ts">
import { productRepository } from '~/repository/v2/productRepository'
import ProductListFacetTreeNode from './ProductListFacetTreeNode.vue'
import type { GetCategoryResponse } from '~/types/apis/v2/getCategory'

const route = useRoute()
const { $serverApi } = useNuxtApp()
const { locale } = useI18n()

const props = defineProps<{
  facetData: Record<string, FacetTree>
}>()

const productRepo = productRepository($serverApi)
const { updateCategory, togglePersonalizable } = useProductFilterStore()
const state = useProductFilterStore()
const { personalizable } = storeToRefs(state)

const isLoading = ref(true)

await useAsyncData<GetCategoryResponse['data'] | null>(
  'GetCategory',
  async () => {
    const response = await productRepo.getCategoryAll()

    if (response.data) {
      updateCategory(response.data)
      setTimeout(() => {
        isLoading.value = false
      }, 500)
      return response.data
    }
    return null
  },
  { server: false, immediate: true }
)

const handleUpdateQuery = () => {
  const query = route.query
  const personalizable = query.personalizable
  if (personalizable === 'yes') {
    // togglePersonalizable()
  }
}

onMounted(() => {
  handleUpdateQuery()
})
</script>
